'use client';

import React, { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react';
import { HubConnection, HubConnectionBuilder, LogLevel, HttpTransportType } from '@microsoft/signalr';
import { AIBotClient } from '@/types/ai-bot-framework';
import { signalRAuthService } from '@/services/signalr-auth.service';
import { logger } from '@/utils/logger';
import { useAuth } from '@/contexts/auth-context';

interface AIBotContextValue {
  connection: HubConnection | null;
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;
  registerMessageHandler: (eventName: string, handler: (...args: any[]) => void) => void;
  unregisterMessageHandler: (eventName: string, handler?: (...args: any[]) => void) => void;
  reconnect: () => Promise<void>;
}

const AIBotContext = createContext<AIBotContextValue | null>(null);

interface AIBotProviderProps {
  children: ReactNode;
  hubUrl?: string;
  autoConnect?: boolean;
  reconnectDelay?: number;
}

export function AIBotProvider({ 
  children, 
  hubUrl = '/hubs/app',
  autoConnect = true,
  reconnectDelay = 5000
}: AIBotProviderProps) {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  
  // Get auth context
  const auth = useAuth();
  const isAuthenticated = auth?.isAuthenticated() ?? false;
  const isAuthLoading = auth?.isLoading ?? true;
  
  // Message handlers registry
  const messageHandlers = useRef<Map<string, Set<(...args: any[]) => void>>>(new Map());
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const mountedRef = useRef(true);
  const connectToHubRef = useRef<((connection: HubConnection) => Promise<void>) | null>(null);

  // Register message handler
  const registerMessageHandler = useCallback((eventName: string, handler: (...args: any[]) => void) => {
    if (!messageHandlers.current.has(eventName)) {
      messageHandlers.current.set(eventName, new Set());
    }

    messageHandlers.current.get(eventName)!.add(handler);
  }, []);

  // Unregister message handler
  const unregisterMessageHandler = useCallback((eventName: string, handler?: (...args: any[]) => void) => {
    if (handler) {
      messageHandlers.current.get(eventName)?.delete(handler);
    } else {
      messageHandlers.current.delete(eventName);
    }
  }, []);

  // Route messages to registered handlers
  const routeMessage = useCallback((eventName: string, ...args: any[]) => {
    const handlers = messageHandlers.current.get(eventName);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(...args);
        } catch (_error) {
          console.error(`Error in ${eventName} handler:`, _error);
        }
      });
    }
  }, []);

  // Create SignalR connection
  const createConnection = useCallback((): HubConnection => {
    // Determine the full URL for the hub
    const getFullHubUrl = () => {
      // In production, use the public API URL directly
      // if (process.env.NODE_ENV === 'production') {
      //   return 'https://api.spearfish.io/hubs/app';
      // }

      // In development, check for environment variables first
      const apiUrl = process.env.NEXT_PUBLIC_API_SERVER_URL ||
                     process.env.API_SERVER_URL;
      
      if (apiUrl) {
        return `${apiUrl}${hubUrl}`;
      }
      
      // Default to localhost for development
      return `http://localhost:5067${hubUrl}`;
    };

    const fullHubUrl = getFullHubUrl();
    logger.info(`Creating SignalR connection to: ${fullHubUrl}`, 'AIBotProvider');
    
    const newConnection = new HubConnectionBuilder()
      .withUrl(fullHubUrl, {
        // Use JWT token instead of cookies
        accessTokenFactory: async () => {
          try {
            const token = await signalRAuthService.getToken();
            logger.debug('SignalR token obtained for AI Bot connection', 'AIBotProvider');
            return token;
          } catch (_error) {
            logger.error('Failed to get SignalR token', 'AIBotProvider', _error);
            throw _error;
          }
        },
        headers: {
          'X-Client-Type': 'web-portal',
          'X-Client-Version': process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0'
        },
        // Specify transports (WebSockets preferred)
        transport: HttpTransportType.WebSockets | HttpTransportType.ServerSentEvents
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: retryContext => {
          // Exponential backoff: 0, 2, 10, 30 seconds, then 30 seconds
          if (retryContext.previousRetryCount === 0) return 0;
          if (retryContext.previousRetryCount === 1) return 2000;
          if (retryContext.previousRetryCount === 2) return 10000;
          return 30000;
        }
      })
      .configureLogging(LogLevel.Information)
      .build();

    // Connection event handlers
    newConnection.onreconnecting((_error) => {
      setIsConnected(false);
      setIsConnecting(true);
      setConnectionError(null);
    });

    newConnection.onreconnected((_connectionId) => {
      setIsConnected(true);
      setIsConnecting(false);
      setConnectionError(null);
    });

    newConnection.onclose((error) => {
      setIsConnected(false);
      setIsConnecting(false);
      
      if (error) {
        setConnectionError(error.message || 'Connection closed with error');
        
        // Schedule reconnection if mounted
        if (mountedRef.current && autoConnect) {
          reconnectTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current && connectToHubRef.current) {
              connectToHubRef.current(newConnection);
            }
          }, reconnectDelay);
        }
      }
    });

    // Register message routing
    const clientMethods: (keyof AIBotClient)[] = [
      'ReceiveBotMessage',
      'ReceiveActionResponse',
      'BotSessionEstablished',
      'BotSwitched',
      'SessionTerminated',
      'AvailableBotsUpdated'
    ];

    clientMethods.forEach(method => {
      newConnection.on(method, (...args) => {
        routeMessage(method, ...args);
      });
    });

    return newConnection;
  }, [hubUrl, autoConnect, reconnectDelay, routeMessage]); // Removed connectToHub to avoid circular dependency

  // Connect to hub
  const connectToHub = useCallback(async (hubConnection: HubConnection) => {
    if (!mountedRef.current) {
      return;
    }
    
    // Only connect if authenticated
    if (!isAuthenticated) {
      logger.info('AIBotProvider: Not authenticated, skipping connection', 'AIBotProvider');
      return;
    }
    
    try {
      setIsConnecting(true);
      setConnectionError(null);
      
      await hubConnection.start();
      
      if (mountedRef.current) {
        setIsConnected(true);
        setIsConnecting(false);
        setConnectionError(null);
      }
    } catch (_error) {
      console.error('Failed to connect to SignalR hub:', _error);

      if (mountedRef.current) {
        setIsConnected(false);
        setIsConnecting(false);
        setConnectionError(_error instanceof Error ? _error.message : 'Connection failed');
        
        // Schedule retry if auto-connect is enabled
        if (autoConnect) {
          reconnectTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current) {
              connectToHub(hubConnection);
            }
          }, reconnectDelay);
        }
      }
    }
  }, [autoConnect, reconnectDelay, isAuthenticated]);

  // Store connectToHub in ref to avoid circular dependency
  useEffect(() => {
    connectToHubRef.current = connectToHub;
  }, [connectToHub]);

  // Manual reconnect function
  const reconnect = useCallback(async () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }

    if (connection) {
      try {
        await connection.stop();
      } catch (_error) {
        console.error('Error stopping connection:', _error);
      }
    }

    const newConnection = createConnection();
    setConnection(newConnection);
    await connectToHub(newConnection);
  }, [connection, createConnection, connectToHub]);

  // Initialize connection
  useEffect(() => {
    // Don't try to connect while auth is loading or if not authenticated
    if (isAuthLoading || !isAuthenticated) {
      logger.info('AIBotProvider: Waiting for authentication', 'AIBotProvider', { isAuthLoading, isAuthenticated });
      return;
    }

    // Only proceed if we don't have a connection and autoConnect is enabled
    if (autoConnect && !connection) {
      const newConnection = createConnection();
      setConnection(newConnection);
      connectToHub(newConnection);
    }

    // Cleanup function - don't set mountedRef here as it affects other effects
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
     
  }, [isAuthLoading, isAuthenticated, autoConnect, connection, createConnection, connectToHub]); // Add missing deps

  // Set mounted ref
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
    };
  }, []);
  
  // Clean up connection on unmount
  useEffect(() => {
    return () => {
      if (connection) {
        connection.stop().catch(error => {
          console.error('Error stopping connection on unmount:', error);
        });
      }
    };
  }, [connection]);

  const contextValue: AIBotContextValue = {
    connection,
    isConnected,
    isConnecting,
    connectionError,
    registerMessageHandler,
    unregisterMessageHandler,
    reconnect
  };

  return (
    <AIBotContext.Provider value={contextValue}>
      {children}
    </AIBotContext.Provider>
  );
}

export function useAIBotProvider(): AIBotContextValue {
  const context = useContext(AIBotContext);
  if (!context) {
    throw new Error('useAIBotProvider must be used within an AIBotProvider');
  }
  return context;
}